// TODO: move layoutHelper in portal into common lib to share with Jupiter

import type { Edge, Node, ReactFlowState } from '@xyflow/react';
import { getNodesBounds, Position } from '@xyflow/react';
import dagre from 'dagre';

import { toPythonicIdentifier } from '@polyai/common/lib/stringHelper';
import type { Flow } from '@polyai/stream/store/reducers/flows';
import type { OptionsType } from '@polyai/ui/components/molecules/Filter/Filter.constants';

import type { ReactFlowNode } from 'screens/FlowDetails/hooks/helpers';

type NodeLookup = ReactFlowState['nodeLookup'];

export const findFlowElementDimensions = (nodes: NodeLookup, path: string) => {
  const node = nodes.get(path);
  if (node) {
    return {
      x: node.position.x,
      y: node.position.y,
      height: node.measured.height || 0,
      width: node.measured.width || 0,
    };
  }

  throw new Error(`Could not find a node with path: ${path}`);
};

function isIntersecting(node1: Node, node2: Node) {
  return !(
    node2.position.x > node1.position.x + (node1.measured?.width ?? 0) ||
    node2.position.x + (node2.width ?? 0) < node1.position.x ||
    node2.position.y > node1.position.y + (node1.measured?.height ?? 0) ||
    node2.position.y + (node2.height ?? 0) < node1.position.y
  );
}

function getIntersectingNodes(nodes: Node[], newNode: Node) {
  return nodes.filter(node => isIntersecting(node, newNode));
}

export const positionNewNode = (
  newNode: Node,
  nodes: Node[],
  existingNodeId: string,
  margin = 100,
) => {
  const existingNode = nodes.find(node => node.id === existingNodeId);

  if (!existingNode) {
    throw new Error('Existing node not found');
  }

  // Find optimal position
  const potentialNode: Node = {
    height: 145,
    width: 350,
    ...newNode,
    position: {
      x: existingNode.position.x,
      y:
        existingNode.position.y + (existingNode.measured?.height ?? 0) + margin,
    },
  };
  let intersectingNodes = getIntersectingNodes(nodes, potentialNode);

  while (intersectingNodes.length) {
    // Move to the right if overlapping
    const nodesRect = getNodesBounds(intersectingNodes);
    potentialNode.position.x = nodesRect.x + nodesRect.width + margin;
    potentialNode.position.y = nodesRect.y;
    intersectingNodes = getIntersectingNodes(nodes, potentialNode);
  }

  return potentialNode;
};

export const getRelativeNodePosition = (
  nodes: Node[],
  existingNodeId: string,
  offsetX: number,
  offsetY: number,
) => {
  const existingNode = nodes.find(node => node.id === existingNodeId);

  if (!existingNode) {
    throw new Error('Existing node not found');
  }
  // add pixels between 20 and 50 to avoid intersecting with other nodes
  const randomNumber = Math.random() * (50 - 20) + 20;

  return {
    x: existingNode.position.x + offsetX + randomNumber,
    y: existingNode.position.y + offsetY + randomNumber,
  };
};

export const layoutEntireGraph = (
  nodes: ReactFlowNode[],
  edges: Edge[],
  flowNodes: NodeLookup,
) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  dagreGraph.setGraph({ rankdir: 'TB', ranksep: 100, nodesep: 100 });

  nodes.forEach(node => {
    const { height, width } =
      findFlowElementDimensions(flowNodes, node.id) || 300;
    dagreGraph.setNode(node.id, {
      width: width,
      height: height,
    });
  });

  edges.forEach((edge: Edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  return nodes.map((node: ReactFlowNode) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    const { height, width } =
      findFlowElementDimensions(flowNodes, node.id) || 300;

    return {
      ...node,
      targetPosition: Position.Top,
      sourcePosition: Position.Bottom,
      position: {
        x: nodeWithPosition.x - width / 2,
        y: nodeWithPosition.y - height / 2,
      },
    };
  });
};

export const hasConnection = (nodeId: string, edges: Edge[]) => {
  const filtered = edges.filter(
    edge => edge.target === nodeId || edge.source === nodeId,
  );

  if (filtered.length) {
    return true;
  }

  return false;
};

export const getUniqueStepName = (name: string, nodes: ReactFlowNode[]) => {
  const names = nodes.map(node => node.data.name);
  const nameSet = new Set(names);

  let suffixNum = 1;
  let uniqueName = name;

  while (nameSet.has(uniqueName)) {
    uniqueName = `${name} ${suffixNum}`;
    suffixNum++;
  }

  return uniqueName;
};

export const getUniqueEntityName = (existingNames: string[]) => {
  const baseName = 'Entity';
  let counter = 1;
  let uniqueName = baseName;

  while (existingNames.includes(uniqueName)) {
    uniqueName = `${baseName} ${counter}`;
    counter++;
  }

  return uniqueName;
};

export const isNameUnique = (flows: Flow[], name: string) =>
  !flows?.find(f => f.name === name);

export const isDownstreamNode = (
  sourceNodeId: string,
  targetNodeId: string,
  edges: Edge[],
): boolean => {
  const visited = new Set<string>();
  const queue = [sourceNodeId];

  while (queue.length > 0) {
    const currentNodeId = queue.shift()!;

    if (visited.has(currentNodeId)) {
      continue;
    }
    visited.add(currentNodeId);

    // Find all outgoing edges from current node
    const outgoingEdges = edges.filter(edge => edge.source === currentNodeId);

    for (const edge of outgoingEdges) {
      if (edge.target === targetNodeId) {
        return true; // Found a path from source to target
      }

      if (!visited.has(edge.target)) {
        queue.push(edge.target);
      }
    }
  }

  return false; // No path found from source to target
};

export const isReachableFromStartStep = (
  nodeId: string,
  nodes: ReactFlowNode[],
  edges: Edge[],
): boolean => {
  const startStepNode = nodes.find(node => node.data.isStartStep);
  if (!startStepNode) {
    return false; // No start step found
  }

  // Check if there's a path from start step to source node
  return isDownstreamNode(startStepNode.id, nodeId, edges);
};

export const generateTimeOptions = (
  startHour: number = 9,
  endHour: number = 8,
  intervalMinutes: number = 15,
): OptionsType[] => {
  const options: OptionsType[] = [];

  // Convert time to minutes for easier calculation
  const startMinutes = startHour * 60;
  const endMinutes =
    endHour <= startHour
      ? (endHour + 24) * 60 + intervalMinutes * 3
      : endHour * 60;

  for (
    let minutes = startMinutes;
    minutes <= endMinutes;
    minutes += intervalMinutes
  ) {
    // hours in 24-hour format
    const hours = Math.floor(minutes / 60) % 24;
    const minute = minutes % 60;

    // Format as 24-hour time (HH:MM) for value
    const value = `${hours.toString().padStart(2, '0')}:${minute
      .toString()
      .padStart(2, '0')}`;

    // Format as 12-hour time for label
    const hoursLabel = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const minuteStr = minute === 0 ? '00' : minute.toString().padStart(2, '0');
    const label = `${hoursLabel}:${minuteStr} ${hours < 12 ? 'AM' : 'PM'}`;

    options.push({ value, label });
  }

  return options;
};

export const getFunctionStepCode = ({ name }: { name: string }) => {
  const pythonicName = toPythonicIdentifier(name);
  return `def ${pythonicName}(conv: Conversation, flow: Flow):

    condition_1 = False
    if condition_1:
        pass
`;
};
